import { useEffect, useMemo, useRef, useState } from 'react';
import * as exifr from 'exifr';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { config } from '../lib/config';
import { acledPosts } from '../lib/acled';
import { Icon } from '../components/Icon';
import type { IconName } from '../types/ui';
import { SunShadowTool } from '../components/SunShadowTool';

type ToolTab = 'exif' | 'reverse' | 'timeline' | 'sun';

/* ---------- helpers ---------- */

const enc = encodeURIComponent;
const openLink = (url: string) => window.open(url, '_blank', 'noopener');

function MapPin({ lat, lng }: { lat: number; lng: number }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !config.mapboxToken) return;
    mapboxgl.accessToken = config.mapboxToken;
    const map = new mapboxgl.Map({
      container: host,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [lng, lat],
      zoom: 12,
      attributionControl: false,
    });
    new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map);
    return () => map.remove();
  }, [lat, lng]);
  if (!config.mapboxToken) return null;
  return <div ref={hostRef} className="exif-map" />;
}

/* ---------- EXIF tool ---------- */

function ExifTool() {
  const [tags, setTags] = useState<Record<string, unknown> | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File) => {
    setBusy(true);
    setError('');
    setTags(null);
    setName(file.name);
    try {
      const all = await exifr.parse(file, true);
      if (!all) {
        setError('No EXIF metadata found in this file. It may have been stripped or re-encoded.');
        return;
      }
      setTags(all as Record<string, unknown>);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file.');
    } finally {
      setBusy(false);
    }
  };

  const lat = typeof tags?.latitude === 'number' ? tags.latitude : undefined;
  const lng = typeof tags?.longitude === 'number' ? tags.longitude : undefined;

  const rows = tags
    ? Object.entries(tags).filter(
        ([k, v]) => v !== undefined && v !== null && !['Make', 'Model'].includes(k),
      )
    : [];

  return (
    <div className="tool-grid">
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">EXIF / METADATA</span>
        </div>
        <p className="dim">
          Upload a photo or video frame. Extracts camera, timestamp and GPS metadata — the first step of every OSINT
          media check.
        </p>
        <label className="upload">
          <Icon name="image" size={18} />
          <span>{name || 'Choose image file…'}</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
        </label>
        {busy && <p className="dim">Parsing…</p>}
        {error && <p className="error-text">{error}</p>}
        {lat !== undefined && lng !== undefined && (
          <div className="exif-gps">
            <span className="event-type event-type--green">GPS FOUND</span>
            <span className="mono">{lat.toFixed(5)}, {lng.toFixed(5)}</span>
            <div className="row">
              <button className="chip" onClick={() => openLink(`https://maps.google.com/?q=${lat},${lng}`)}>
                Google Maps
              </button>
              <button className="chip" onClick={() => openLink(`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=14/${lat}/${lng}`)}>
                OSM
              </button>
            </div>
            <MapPin lat={lat} lng={lng} />
          </div>
        )}
      </div>
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">TAG DUMP</span>
          <span className="mono dim">{rows.length} tags</span>
        </div>
        {tags ? (
          <div className="tagdump">
            {Object.entries(tags)
              .filter(([, v]) => v !== undefined && v !== null)
              .map(([k, v]) => (
                <div key={k} className="tag-row">
                  <span className="tag-row__k mono">{k}</span>
                  <span className="tag-row__v">{String(v)}</span>
                </div>
              ))}
          </div>
        ) : (
          <div className="empty-state">No file parsed yet.</div>
        )}
      </div>
    </div>
  );
}

/* ---------- Reverse search ---------- */

function ReverseTool() {
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState('');
  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
  };
  const target = url.trim() || preview;
  const engines = [
    { label: 'Google Lens', href: `https://lens.google.com/uploadbyurl?url=${enc(target)}` },
    { label: 'Yandex', href: `https://yandex.com/images/search?rpt=imageview&url=${enc(target)}` },
    { label: 'Bing Visual', href: `https://www.bing.com/visualsearch?q=imgurl:${enc(target)}` },
    { label: 'TinEye', href: `https://tineye.com/search?url=${enc(target)}` },
  ];
  return (
    <div className="tool-grid">
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">REVERSE SEARCH HUB</span>
        </div>
        <p className="dim">
          Paste an image URL (hosted) or upload a local file for preview. Search engines need a reachable URL — for
          local files, upload them to lens.google.com or tineye.com directly.
        </p>
        <label className="field">
          <span className="mono dim">IMAGE URL</span>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/photo.jpg" />
        </label>
        <label className="upload">
          <Icon name="image" size={18} />
          <span>Upload local preview…</span>
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        </label>
        {preview && <img src={preview} alt="preview" className="rev-preview" />}
        {!target && <div className="empty-state">Provide an image URL to enable search.</div>}
        {target && (
          <div className="row wrap">
            {engines.map((e) => (
              <button key={e.label} className="chip chip--on" onClick={() => openLink(e.href)}>
                <Icon name="link" size={13} /> {e.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">PRO TIPS</span>
        </div>
        <ul className="tips">
          <li>Stripped EXIF? Compare <b>noise patterns</b> and <b>sensor dust</b> against known cameras.</li>
          <li>Shadow length + sun position → approximate <b>time of day</b> (see Cases → Chrono-Locate).</li>
          <li>Billboards, road signs, power poles: search the text, not the scene.</li>
          <li>Yandex finds duplicates that Google misses — run both.</li>
          <li>Frames from videos: extract with VLC/ffmpeg, then run EXIF + reverse search.</li>
        </ul>
      </div>
    </div>
  );
}

/* ---------- Timeline builder ---------- */

interface TimelineRow { id: string; date: string; title: string; source: string; note: string }

function TimelineTool() {
  const seed = useMemo<TimelineRow[]>(
    () =>
      (acledPosts()).map((p, i) => ({
        id: `seed-${i}`,
        date: p.date,
        title: p.title,
        source: p.src,
        note: p.type,
      })),
    [],
  );
  const [rows, setRows] = useState<TimelineRow[]>(seed);
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const add = () => {
    if (!title.trim()) return;
    setRows((r) => [...r, { id: `r-${Date.now()}`, date: date || '—', title: title.trim(), source: source.trim() || 'manual', note: 'manual' }]);
    setTitle('');
    setSource('');
    setDate('');
  };
  const sorted = useMemo(() => [...rows].sort((a, b) => a.date.localeCompare(b.date)), [rows]);
  const exportMd = () => {
    const md = ['# Fall-Akte — Timeline', '', ...sorted.map((r) => `- **${r.date}** — ${r.title} _(${r.source})_`), ''].join('\n');
    const blob = new Blob([md], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `timeline-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="tool-grid">
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">TIMELINE BUILDER</span>
          <button className="chip" onClick={exportMd} disabled={!sorted.length}>
            Export .md
          </button>
        </div>
        <p className="dim">Chain events into a case file. Seeded with the current ACLED feed; add your own rows.</p>
        <div className="timeline-form">
          <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="YYYY-MM-DD" className="tl-date" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" />
          <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source" />
          <button className="btn" onClick={add}>
            <Icon name="plus" size={14} /> Add
          </button>
        </div>
        <div className="timeline">
          {sorted.map((r) => (
            <div key={r.id} className="tl-row">
              <span className="mono tl-row__date">{r.date}</span>
              <div>
                <strong>{r.title}</strong>
                <span className="dim">{r.source}</span>
              </div>
              <button
                className="chip"
                onClick={() => setRows((prev) => prev.filter((x) => x.id !== r.id))}
                aria-label="remove"
              >
                ×
              </button>
            </div>
          ))}
          {!sorted.length && <div className="empty-state">No events yet.</div>}
        </div>
      </div>
      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">CASE-FILE LOGIC</span>
        </div>
        <ul className="tips">
          <li>A case file needs: <b>time anchor</b>, <b>place anchor</b>, <b>source chain</b>.</li>
          <li>Sort by time, then check each hop: does the source actually show what the previous row implies?</li>
          <li>Export as Markdown and attach it to a Forum post or Case submission.</li>
          <li>Contradictions belong in the file — they are evidence too.</li>
        </ul>
      </div>
    </div>
  );
}

/* ---------- page ---------- */

const TABS: { id: ToolTab; label: string; icon: IconName }[] = [
  { id: 'exif', label: 'EXIF', icon: 'image' },
  { id: 'reverse', label: 'Reverse Search', icon: 'search' },
  { id: 'timeline', label: 'Timeline', icon: 'clock' },
  { id: 'sun', label: 'Sun / Shadow', icon: 'target' },
];

export function ToolsPage() {
  const [tab, setTab] = useState<ToolTab>('exif');
  return (
    <div className="page" data-testid="tools-page">
      <section className="page-intro">
        <div>
          <span className="eyebrow">Analyst workbench</span>
          <h1>Tools</h1>
          <p>The missing OSINT workbench — in the browser, no setup.</p>
        </div>
      </section>
      <nav className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`tab-btn${tab === t.id ? ' tab-btn--on' : ''}`} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={15} /> {t.label}
          </button>
        ))}
      </nav>
      {tab === 'exif' && <ExifTool />}
      {tab === 'reverse' && <ReverseTool />}
      {tab === 'timeline' && <TimelineTool />}
      {tab === 'sun' && <SunShadowTool />}
    </div>
  );
}
